import ThemedDialog from 'Components/_NewElements/Dialog';
import ActionButton from 'Components/ActionButton';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import React from 'react';
import { useIntl } from 'react-intl';
import { subjectOfTreatmentNames } from 'Routes/DoctorsOrders/Document/config';
import { actions } from 'Store/documents/actions';
import { useAppDispatch } from 'Store/index';
import ChangeDialog from './ChangeDialog';
import { getFile } from 'Store/documents/helpers/apiFetchers';

export const getLocalizedSubject = (messages: Record<string, string>) => (name: string) => {
  const messageKeys = Object.keys(messages);
  const subjectLocaleKeys = messageKeys.filter(m => m.includes('rtms.subjectOfTreatmentNames.'));

  const localeKey = subjectLocaleKeys.find(key => key === 'rtms.subjectOfTreatmentNames.' + name);
  if (localeKey) {
    return messages[localeKey] as string;
  }
  return name;
};

const EditDialog = ({ editingId, editingFileData, onCloseEditing, newData, editFileData }: IEditDialog) => {
  const [newFile, setNewFile] = React.useState<File | true | null>(null);
  const [imageBlob, setImageBlob] = React.useState<Blob | null>(null);

  const dispatch = useAppDispatch();

  const { formatMessage, messages } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  React.useEffect(() => {
    if (editingId && !imageBlob && editingFileData?.fileContentId) {
      void getFile(editingId, editingFileData.fileContentId).then(res => {
        if (res) setImageBlob(res);
      });
    }
  }, [editingId]);

  const onCloseEditingThis = () => {
    onCloseEditing();
    setImageBlob(null);
    setNewFile(null);
  };

  const getLocalizedSubjectName = getLocalizedSubject(messages as Record<string, string>);

  // Enable editing the reference field by textbox if the value is 'other', or other than the imported list of subjects
  const subjectNameIsOther = () => {
    if (!newData?.['referenceType'] && !editingFileData?.['referenceType']) return false;
    const dataName = newData?.['referenceType'] ?? editingFileData?.['referenceType'];
    const localizedName = getLocalizedSubjectName(dataName!);
    if (dataName === localizedName || dataName === 'other') return true;
    return false;
  };

  const previewImage = newFile && typeof newFile === 'object' ? newFile : imageBlob;

  return (
    <>
      <ThemedDialog
        open={!!editingId}
        dialogActions={[
          {
            text: 'general.cancel',
            onClick: () => {
              onCloseEditingThis();
            },
            border: false,
          },
          {
            text: 'general.confirm',
            onClick: async () => {
              let error = null;

              if (editingId) {
                if (newData && Object.keys(newData).length > 0) {
                  void (await actions
                    .editFileMetadata(
                      editingId,
                      newData,
                    )(dispatch)
                    .then(res => {
                      if (!res) error = 'error';
                    }));
                }
                if (newFile && typeof newFile === 'object') {
                  void (await actions
                    .editFileContent(
                      newFile,
                      editingId,
                    )(dispatch)
                    .then(res => {
                      if (!res) error = 'error';
                    }));
                }
              }

              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              if (!error) {
                onCloseEditingThis();
              }
            },
          },
        ]}
      >
        <div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {previewImage ? (
              <img style={{ maxWidth: '90%', maxHeight: '50rem' }} src={URL.createObjectURL(previewImage)} />
            ) : (
              <></>
            )}
          </div>
          {newFile && typeof newFile === 'object' && 'name' in newFile ? (
            <div style={{ margin: '4rem 0', display: 'flex', justifyContent: 'center' }}>
              {fm('rtms.subjectOfTreatment.newPicture')}
              <span style={{ marginLeft: '2rem', fontWeight: 600 }}>{newFile.name}</span>
            </div>
          ) : (
            <></>
          )}
          <div style={{ margin: '4rem 0', display: 'flex', justifyContent: 'center' }}>
            <ActionButton
              text="rtms.subjectOfTreatment.changePicture"
              width="auto"
              height={3}
              fontSize={14}
              onClick={() => setNewFile(true)}
            />
          </div>
          <FormRow title="rtms.subjectOfTreatment.name">
            <InputHandler
              editing
              name="referenceType"
              type="Select"
              placeholder={'general.choose'}
              options={[...subjectOfTreatmentNames, 'supportPicture']}
              optionFormatter={o =>
                o ? (
                  <React.Fragment key={fm(`rtms.subjectOfTreatmentNames.${o}`)}>
                    {fm(`rtms.subjectOfTreatmentNames.${o}`)}
                  </React.Fragment>
                ) : (
                  ''
                )
              }
              formData={{
                document: {
                  referenceType: subjectNameIsOther()
                    ? 'other'
                    : (newData?.['referenceType'] ?? editingFileData?.['referenceType']),
                },
                onChange: values => {
                  const value = values['referenceType'] as string;
                  if (value === 'other' && subjectNameIsOther()) {
                    return;
                  } else {
                    editFileData({ referenceType: value });
                  }
                },
              }}
            />
          </FormRow>
          {subjectNameIsOther() && (
            <FormRow title="rtms.subjectOfTreatmentNames.other">
              <InputHandler
                editing
                name="referenceType"
                type="TextField"
                formData={{
                  document: { referenceType: newData?.['referenceType'] ?? editingFileData?.['referenceType'] },
                  onChange: values => {
                    const value = values['referenceType'] as string;
                    editFileData({ referenceType: value });
                  },
                }}
                placeholder={'rtms.subjectOfTreatment.name'}
              />
            </FormRow>
          )}
          <FormRow title="general.fileName">
            <InputHandler
              editing
              name="name"
              type="TextField"
              formData={{
                document: { name: newData?.['name'] ?? editingFileData?.['name'] },
                onChange: values => {
                  const value = values['name'] as string;
                  editFileData({ name: value });
                },
              }}
              width={50}
            />
          </FormRow>
        </div>
      </ThemedDialog>
      <ChangeDialog dialogOpen={newFile === true} setNewAttachment={file => setNewFile(file)} />
    </>
  );
};

interface IEditDialog {
  editingId: string | null;
  editingFileData: IDocumentWithTypeV2<'filestorefile'> | null;
  onCloseEditing: () => void;
  newData: Partial<Pick<TFILE_METADATA, 'date' | 'name' | 'subType' | 'referenceType'>> | null;
  editFileData: (data: Record<string, string>) => void;
}

export default EditDialog;
